/**
 * SF-02 Contraction deficit (INV-CH-SF-02, ENGINE-SPEC §4). Kept from the seed unchanged:
 * CONTRACTION_RE hits per 1,000 prose words, two-way, descending ramp (fewer contractions read
 * as more machine-like). Events and spans come from one matcher run per prose block.
 */
import { REGISTER_PROSE } from '../document/types.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons, safeScan } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseParagraphs } from './helpers.ts';
import type { ChannelDef, Measurement, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0, 68],
	[2, 52],
	[6, 34],
	[12, 20],
	[20, 10],
];

export const sf02Def: ChannelDef = {
	id: 'SF-02',
	name: 'Contraction deficit',
	family: 'TEXTURE',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'rate',
	features: [{ id: 'contraction', label: 'Contractions per 1,000 words' }],
	minExposure: 400,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1000,
	unitLabel: 'per 1k words',
	prior: derivePrior(RAMP, 1000, 'words', 1.5),
	weight: 0.6,
	nd: 1,
	changes: [],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const L = lex();
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	let n = 0;
	for (const p of proseParagraphs(doc)) {
		if (doc.paragraphs.register[p] !== REGISTER_PROSE) continue;
		e[p] = doc.paragraphs.ownWords[p] as number;
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		// the prefilter literals are the two apostrophes: case folding is irrelevant
		if (!L.contraction.admits(0, text, text)) continue;
		safeScan(L.contraction.regexes[0] as RegExp, text, b.charStart, (s, en) => {
			ev.add(s, en, 0, 1);
			k[p] = (k[p] as number) + 1;
			n++;
		});
	}
	const words = doc.proseWords;
	const rate = words > 0 ? (n / words) * 1000 : 0;
	return {
		k,
		e,
		evidence: ev,
		summary: [`${n} contractions · ${fmt(rate, 1)}/1000w`],
	};
}

export const sf02 = rateChannel(sf02Def, measure);
export { sf02Def as def, sf02 as impl };
